import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, CheckCircle2, Hash, Sparkles } from "lucide-react";
import KioskLayout from "@/kiosk/KioskLayout";
import PhotoCapture from "@/kiosk/PhotoCapture";
import SignaturePad from "@/kiosk/SignaturePad";
import kioskApi, { getKioskToken } from "@/lib/kioskApi";
import { sanitizeHtml } from "@/lib/safeHtml";
import axios from "axios";
import { API } from "@/lib/api";

const STEPS = ["type", "details", "photo", "signature", "consents", "submit", "done"];

function readKioskToken() {
  const tok = getKioskToken();
  if (!tok) return null;
  try {
    return JSON.parse(atob(tok.split(".")[1]));
  } catch {
    return null;
  }
}

export default function KioskSignIn() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const preRegToken = searchParams.get("prereg");
  const tokenInfo = readKioskToken();
  const [branding, setBranding] = useState(null);
  const [types, setTypes] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [consents, setConsents] = useState([]);
  const [step, setStep] = useState(0);
  const [preReg, setPreReg] = useState(null);
  const [form, setForm] = useState({
    visitor_type: null,
    visitor_name: "",
    visitor_company: "",
    visitor_email: "",
    visitor_phone: "",
    host_id: "",
    purpose: "",
    vehicle_reg: "",
    photo_data_url: null,
    photo_bypass: false,
    signature_data_url: null,
    accepted_consents: {},
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!tokenInfo) {
      nav("/kiosk/pair", { replace: true });
      return;
    }
    (async () => {
      try {
        const [tRes, cRes, bRes] = await Promise.all([
          kioskApi.get("/visitor-types/active"),
          kioskApi.get("/consent-documents/active"),
          axios.get(`${API}/sites/${tokenInfo.site_id}/public-branding`),
        ]);
        setTypes(tRes.data);
        setConsents(cRes.data);
        setBranding(bRes.data);
        // Phase 2 — pre-reg fast-flow: validate the token and prefill (5.3).
        if (preRegToken) {
          try {
            const { data: pr } = await kioskApi.get(`/pre-registrations/by-token/${preRegToken}`);
            setPreReg(pr);
            setForm((f) => ({
              ...f,
              visitor_type: pr.visitor_type || f.visitor_type,
              visitor_name: pr.visitor_name || "",
              visitor_company: pr.company || "",
              visitor_email: pr.visitor_email || "",
              visitor_phone: pr.visitor_phone || "",
              host_id: pr.host_id || "",
              purpose: pr.purpose || "",
            }));
            // Skip past type + details — go straight to photo / signature / consents
            setStep(STEPS.indexOf("photo"));
          } catch (e) {
            setErr(e.response?.data?.detail || "Pre-registration token invalid or expired");
          }
        }
      } catch (e) {
        setErr(e.response?.data?.detail || "Failed to load kiosk data");
      }
    })();
  }, [nav, tokenInfo?.site_id, preRegToken]);

  const selectedType = useMemo(() => types.find((x) => x.name === form.visitor_type), [form.visitor_type, types]);
  const required = selectedType?.required_fields || [];

  // ----- helpers -----
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const lookupReturning = async () => {
    if (!form.visitor_email && !form.visitor_phone) return;
    try {
      const params = {};
      if (form.visitor_email) params.email = form.visitor_email;
      if (form.visitor_phone) params.phone = form.visitor_phone;
      // Prefer visitors_directory which carries last_visitor_type + last_consents
      try {
        const dir = await kioskApi.get("/visitors-directory/lookup", { params });
        if (dir.data) {
          const r = dir.data;
          setForm((f) => ({
            ...f,
            visitor_name: f.visitor_name || r.name || "",
            visitor_company: f.visitor_company || r.company || "",
            host_id: f.host_id || r.last_host_id || "",
            visitor_type: f.visitor_type || r.last_visitor_type || null,
          }));
          return;
        }
      } catch { /* fall back below */ }
      const { data } = await kioskApi.get("/visits/lookup", { params });
      if (data && data.length > 0) {
        const r = data[0];
        setForm((f) => ({
          ...f,
          visitor_name: f.visitor_name || r.visitor_name || "",
          visitor_company: f.visitor_company || r.visitor_company || "",
          host_id: f.host_id || r.host_id || "",
        }));
      }
    } catch {
      /* ignore */
    }
  };

  // ----- step views -----
  const stepView = STEPS[step];

  const requiresPhoto = required.includes("photo");
  const requiresSignature = required.includes("signature");
  const requiresConsents = required.includes("consents");

  const submit = async () => {
    setErr("");
    setSubmitting(true);
    try {
      const consentsArr = consents
        .filter((c) => form.accepted_consents[c.id])
        .map((c) => ({ doc_id: c.id }));
      const { data } = await kioskApi.post("/visits/sign-in", {
        visitor_type: form.visitor_type,
        visitor_name: form.visitor_name,
        visitor_company: form.visitor_company || null,
        visitor_email: form.visitor_email || null,
        visitor_phone: form.visitor_phone || null,
        host_id: form.host_id || null,
        purpose: form.purpose || null,
        vehicle_reg: form.vehicle_reg || null,
        photo_data_url: form.photo_data_url,
        photo_bypass: form.photo_bypass,
        signature_data_url: form.signature_data_url,
        consents: consentsArr.length ? consentsArr : null,
        pre_reg_token: preRegToken || null,
      });
      setResult(data);
      setStep(STEPS.indexOf("done"));
      // Auto-return after 7s
      setTimeout(() => nav("/kiosk", { replace: true }), 7000);
    } catch (e) {
      setErr(e.response?.data?.detail || "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <KioskLayout branding={branding}>
      <div className="flex items-center justify-between mb-6 text-white/70 text-sm" data-testid="kiosk-sign-in-page">
        <button onClick={() => (step === 0 ? nav("/kiosk", { replace: true }) : prev())} className="inline-flex items-center gap-2 hover:text-white" data-testid="kiosk-back-btn">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </button>
        <div className="flex gap-1">
          {STEPS.slice(0, -1).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 w-8 rounded-full ${i < step ? "bg-white" : i === step ? "bg-orange-400" : "bg-white/15"}`}
            />
          ))}
        </div>
        <button onClick={() => nav("/kiosk", { replace: true })} className="text-xs text-white/50 hover:text-white" data-testid="kiosk-cancel-btn">
          {t("cancel")}
        </button>
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-2xl w-full mx-auto">
        {preReg && (
          <div
            className="mb-6 rounded-2xl bg-orange-500/15 border border-orange-400/30 px-5 py-3 text-sm text-orange-100 inline-flex items-center gap-3"
            data-testid="kiosk-prereg-banner"
          >
            <Sparkles className="w-4 h-4 text-orange-300" />
            <span>Welcome back, <strong className="text-white">{preReg.visitor_name?.split(" ")[0]}</strong> — pre-registered visit, fast-flow.</span>
          </div>
        )}
        {stepView === "type" && (
          <div data-testid="step-type">
            <h2 className="text-3xl md:text-4xl font-semibold mb-8 text-center">{t("visitor_type_choose")}</h2>
            <div className="grid grid-cols-2 gap-4">
              {types.map((tp) => (
                <button
                  key={tp.id}
                  onClick={() => { set("visitor_type", tp.name); next(); }}
                  className="rounded-2xl bg-white/10 hover:bg-white/15 border border-white/20 px-6 py-8 text-center transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{ minHeight: 120 }}
                  data-testid={`type-${tp.name}`}
                >
                  <div className="text-xl font-semibold">{tp.name}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {stepView === "details" && (
          <div className="space-y-4" data-testid="step-details">
            <h2 className="text-3xl font-semibold">{form.visitor_type}</h2>
            <p className="text-white/70 text-sm">Fill in your details. * are required.</p>

            <div className="grid md:grid-cols-2 gap-3">
              <Field label={`${t("name")} *`} value={form.visitor_name} onChange={(v) => set("visitor_name", v)} testid="field-name" />
              {(required.includes("visitor_company") || form.visitor_type === "Visitor") && (
                <Field label={`${t("company")}${required.includes("visitor_company") ? " *" : ""}`} value={form.visitor_company} onChange={(v) => set("visitor_company", v)} testid="field-company" />
              )}
              <Field label={`${t("email")}${required.includes("visitor_email") ? " *" : ""}`} type="email" value={form.visitor_email} onChange={(v) => set("visitor_email", v)} onBlur={lookupReturning} testid="field-email" />
              <Field label={t("phone")} value={form.visitor_phone} onChange={(v) => set("visitor_phone", v)} onBlur={lookupReturning} testid="field-phone" />
              {required.includes("host_id") && (
                <Field label={`${t("host")} *`} value={form.host_id} onChange={(v) => set("host_id", v)} placeholder="Host ID (or paste from admin)" testid="field-host" />
              )}
              {!selectedType?.auto_purpose && (
                <Field label={t("purpose")} value={form.purpose} onChange={(v) => set("purpose", v)} testid="field-purpose" />
              )}
              {form.visitor_type === "Contractor" && (
                <Field label={t("vehicle_reg")} value={form.vehicle_reg} onChange={(v) => set("vehicle_reg", v)} testid="field-vehicle-reg" />
              )}
            </div>

            <div className="flex justify-end pt-3">
              <button
                disabled={!form.visitor_name}
                onClick={next}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-semibold disabled:opacity-50"
                data-testid="kiosk-next-details"
              >{t("next")} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {stepView === "photo" && (
          <div className="text-center" data-testid="step-photo">
            <h2 className="text-3xl font-semibold mb-4">{t("photo")}</h2>
            <PhotoCapture
              onChange={(url) => set("photo_data_url", url)}
              onSkip={() => { set("photo_bypass", true); set("photo_data_url", null); next(); }}
              allowSkip={true}
            />
            <div className="flex justify-end pt-6">
              <button
                disabled={requiresPhoto && !form.photo_data_url && !form.photo_bypass}
                onClick={next}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-semibold disabled:opacity-50"
                data-testid="kiosk-next-photo"
              >{t("next")} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {stepView === "signature" && (
          <div data-testid="step-signature">
            <h2 className="text-3xl font-semibold mb-4 text-center">{t("sign_here")}</h2>
            <SignaturePad onChange={(url) => set("signature_data_url", url)} />
            <div className="flex justify-end pt-4">
              <button
                disabled={requiresSignature && !form.signature_data_url}
                onClick={next}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-semibold disabled:opacity-50"
                data-testid="kiosk-next-signature"
              >{t("next")} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {stepView === "consents" && (
          <div data-testid="step-consents">
            <h2 className="text-3xl font-semibold mb-4">{t("consent_title")}</h2>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {consents.map((c) => (
                <div key={c.id} className="bg-white/5 border border-white/15 rounded-xl p-5" data-testid={`consent-${c.id}`}>
                  <div className="font-semibold mb-2 flex items-center justify-between">
                    {c.title}
                    <span className="text-xs uppercase tracking-wider text-white/50">v{c.version}</span>
                  </div>
                  <div className="text-sm text-white/70 prose prose-invert max-h-[12rem] overflow-y-auto" dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.body_html) }} />
                  <label className="mt-3 inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.accepted_consents[c.id]}
                      onChange={(e) => set("accepted_consents", { ...form.accepted_consents, [c.id]: e.target.checked })}
                      className="w-4 h-4"
                      data-testid={`consent-cb-${c.id}`}
                    />
                    {t("i_accept")}
                  </label>
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-4">
              <button
                disabled={requiresConsents && !consents.every((c) => form.accepted_consents[c.id])}
                onClick={next}
                className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-semibold disabled:opacity-50"
                data-testid="kiosk-next-consents"
              >{t("next")} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {stepView === "submit" && (
          <div className="text-center" data-testid="step-submit">
            <h2 className="text-3xl font-semibold mb-4">Ready to sign in?</h2>
            <p className="text-white/70 mb-6">Tap below to confirm. Your host will be notified.</p>
            {err && <div className="text-red-300 mb-4 text-sm" data-testid="submit-error">{err}</div>}
            <button
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 hover:bg-orange-600 text-white px-10 py-5 text-xl font-semibold"
              data-testid="kiosk-submit-btn"
            >{submitting ? "Signing in…" : t("submit")}</button>
            <div className="mt-6 text-xs text-white/50">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-warning/15 border border-warning/30 text-warning">
                {t("mocked_resend")}
              </span> Host notification will be written to the Notifications log (mock mode).
            </div>
          </div>
        )}

        {stepView === "done" && result && (
          <div className="text-center" data-testid="step-done">
            <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-400/40 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-10 h-10 text-green-300" />
            </div>
            <h2 className="text-4xl font-bold mb-2">{t("success_title")}</h2>
            <p className="text-white/70 mb-6">{t("success_subtitle")}</p>
            <div className="inline-block bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 text-left">
              <div className="text-xs uppercase tracking-wider text-orange-300">Sign-out PIN</div>
              <div className="text-5xl font-bold tracking-[0.4em] mt-2 mb-3" data-testid="signout-pin">{result.sign_out_pin}</div>
              <div className="text-xs text-white/60 flex items-center gap-1.5"><Hash className="w-3 h-3" /> Use this PIN at the kiosk to sign out</div>
            </div>
            <p className="mt-8 text-xs text-white/40">Returning to home in a few seconds…</p>
          </div>
        )}
      </div>
    </KioskLayout>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, onBlur, testid }) {
  return (
    <label className="block">
      <div className="text-xs text-white/70 uppercase tracking-wider mb-1">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-orange-400"
      />
    </label>
  );
}
